import { useState, useEffect, useRef, useCallback } from 'react';
import { EventSourcePolyfill } from 'event-source-polyfill';
import { API_URL } from '@/config';
import storage from '@/utils/storage';

export const useUserCredits = (enabled: boolean) => {
  const [userCredits, setUserCredits] = useState<any>({});
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimerRef = useRef<NodeJS.Timeout | null>(null);
  const previousAccessTokenRef = useRef<string | null>(null);
  const isConnectingRef = useRef<boolean>(false);

  const teamData = storage?.getTeam();
  const xTeamIdFromLocalStorage = teamData ? teamData._id : null;
  const xTeamId = xTeamIdFromLocalStorage;

  const closeExistingConnection = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    isConnectingRef.current = false;
  }, []);

  const establishSSEConnection = useCallback(
    (accessToken: string) => {
      if (!enabled || !xTeamId || !accessToken || isConnectingRef.current) return;

      // Close existing connection before establishing new one
      closeExistingConnection();

      isConnectingRef.current = true;

      const url = `${API_URL}/v1/credit/stream`;
      const newEventSource = new EventSourcePolyfill(url, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'X-Team': xTeamId,
        },
        withCredentials: false,
      });

      eventSourceRef.current = newEventSource;

      newEventSource.onopen = () => {
        isConnectingRef.current = false;
        console.log('SSE connection established');

        if (reconnectTimerRef.current) {
          clearTimeout(reconnectTimerRef.current);
          reconnectTimerRef.current = null;
        }
      };

      newEventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          setUserCredits(data);
        } catch (error) {
          console.error('Error parsing SSE message:', error);
        }
      };

      newEventSource.onerror = (error) => {
        console.error('SSE connection error:', error);
        isConnectingRef.current = false;
        newEventSource.close();

        // Only attempt reconnection if we still have a valid token and are enabled
        reconnectTimerRef.current = setTimeout(() => {
          const currentToken = storage.getToken();
          if (currentToken && enabled && xTeamId) {
            console.log('Attempting to reconnect SSE...');
            establishSSEConnection(currentToken);
          }
        }, 5000);
      };
    },
    [enabled, xTeamId, closeExistingConnection]
  );

  // Function to handle token changes
  const handleTokenChange = useCallback(
    (newToken: string | null) => {
      const previousToken = previousAccessTokenRef.current;

      // Token has changed (including from null to a value, or from a value to null)
      if (previousToken !== newToken) {
        console.log('Access token changed, updating SSE connection');

        // Always close existing connection first
        closeExistingConnection();

        // Update the reference
        previousAccessTokenRef.current = newToken;

        // Establish new connection if we have a token and are enabled
        if (enabled && newToken && xTeamId) {
          establishSSEConnection(newToken);
        }
      }
    },
    [enabled, xTeamId, establishSSEConnection, closeExistingConnection]
  );

  useEffect(() => {
    const currentToken = storage.getToken();

    // Handle token changes
    handleTokenChange(currentToken);

    // If no previous token was set and we have conditions met, establish initial connection
    if (
      previousAccessTokenRef.current === null &&
      enabled &&
      currentToken &&
      xTeamId &&
      !eventSourceRef.current
    ) {
      previousAccessTokenRef.current = currentToken;
      establishSSEConnection(currentToken);
    }

    // If disabled, ensure connection is closed
    if (!enabled) {
      closeExistingConnection();
    }

    return () => {
      closeExistingConnection();
    };
  }, [enabled, xTeamId, handleTokenChange, establishSSEConnection, closeExistingConnection]);

  const terminateSSEConnection = useCallback(() => {
    closeExistingConnection();
  }, [closeExistingConnection]);

  const refreshConnection = useCallback(() => {
    const currentToken = storage.getToken();
    if (currentToken && enabled && xTeamId) {
      closeExistingConnection();
      establishSSEConnection(currentToken);
    }
  }, [enabled, xTeamId, establishSSEConnection, closeExistingConnection]);

  return {
    userCredits,
    establishSSEConnection: () => {
      const currentToken = storage.getToken();
      if (currentToken) {
        establishSSEConnection(currentToken);
      }
    },
    terminateSSEConnection,
    refreshConnection,
    isConnected: eventSourceRef.current?.readyState === EventSource.OPEN,
  };
};
